import { AiToolTypeValueType } from '@lessonup/analytics';
import { UpdatePinDispatch } from '@lessonup/pin-renderer';
import {
  initialPinComponentPosition,
  isPollPin,
  OpenQuestionTeacherPin,
  Pin,
  PinComponent,
  PinCreationParams,
  QuizTeacherPin,
  TextPinComponent,
  Vector,
} from '@lessonup/pins-shared';
import { assertNever } from '@lessonup/utils';
import { TFunction } from 'i18next';
import { SetModeSelecting } from '../../context/EditorContext/EditorContext.types';
import { trackPinComponentCreate } from '../pinComponent/pinComponent.utils';
import { calculateInitialPosition } from '../pinComponent/position';

export const getQuestionComponentInInteractivePin = (
  pin: OpenQuestionTeacherPin | QuizTeacherPin | PinCreationParams<QuizTeacherPin>
): TextPinComponent => {
  const index = getQuestionComponentInInteractivePinIndex(pin);
  return pin.pinComponents[index] as TextPinComponent;
};

export const getQuestionComponentInInteractivePinIndex = (
  pin: OpenQuestionTeacherPin | QuizTeacherPin | PinCreationParams<QuizTeacherPin>
) => {
  return pin.pinComponents.findIndex((pinComponent) => pinComponent.id === pin.settings.questionId);
};

export interface PinComponentConstructor {
  newComponent: (position: Vector) => PinComponent;
  onComplete?: (pinComponent: PinComponent) => void;
  aiToolType?: AiToolTypeValueType;
}

export function createAddPinComponent(
  dispatch: UpdatePinDispatch,
  setModeSelecting: SetModeSelecting,
  pin: Pin | null
): (constructor: PinComponentConstructor) => void {
  return ({ newComponent, onComplete, aiToolType }) => {
    const position = calculateInitialPosition(initialPinComponentPosition, pin?.pinComponents);
    const component = newComponent(position);

    dispatch({
      type: 'addPinComponents',
      pinComponents: [component],
      onComplete: () => {
        setModeSelecting([component.id]);
        onComplete?.(component);
      },
    });

    trackPinComponentCreate(component.type, 'create_own', 1, aiToolType);
  };
}

export function createStaggeredPinComponent(
  pinComponents: PinComponent[],
  newComponent: (position: Vector) => PinComponent
) {
  const position = calculateInitialPosition(initialPinComponentPosition, pinComponents);

  return newComponent(position);
}

/**
 * Get the translation key for the pin label, as used in the pin rail.
 *
 * @param t - The translation function with editor namespace
 * @param pin - The pin to get the label for
 * @returns The translation key for the pin label.
 * Returns the normal slide label as a fallback.
 */
export function getPinLabel(t: TFunction, pin: Pin): string {
  if (isPollPin(pin)) {
    const pollType = pin.settings.type;
    switch (pollType) {
      case 'EMOJI':
        return t('poll.label.emoji');
      case 'TEXT':
        return t('poll.label.text');
      default:
        assertNever(pollType, 'Unknown poll label');
    }
  }

  const pinType = pin.type;
  switch (pinType) {
    case 'UNSUPPORTED':
      return t('unsupported.label');
    case 'OPEN_QUESTION':
      return t('openQuestion.label');
    case 'MULTIPLE_CHOICE':
      return t('multipleChoice.label');
    case 'SLIDE':
      return t('slide.label');
    case 'WORD_CLOUD':
      return t('wordCloud.label');
    case 'ANSWER_BY_CATEGORY':
      return t('answerByCategory.label');
    case 'FILL_IN_THE_BLANKS':
      return t('fillInTheBlanks.label');
    case 'SORTING_QUESTION':
      return t('sortingQuestion.label');
    case 'DRAG_AND_DROP':
      return t('dragAndDrop.label');
    default:
      assertNever(pinType, 'Unknown pin label');
  }
}
